import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { JustSpinner } from 'components/Loading/Loading'
import { Modal } from 'components/Modal/Modal'
import { raiseIncident } from 'features/operations/incidentsSlice'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Button } from 'components/ui/Button/Button'
import { Box, CursorPointer, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
  INCIDENT_SEVERITY_COLORS,
  INCIDENT_SEVERITY_DESCRIPTIONS,
  IncidentSeverity,
  IRaiseIncidentFormState,
} from 'types/incidents.interface'
import useFocusFirstInput from 'utils/useFocusFirstInput'

const SEVERITY_LEVELS: IncidentSeverity[] = ['L1', 'L2', 'L3', 'L4']

const initialState: IRaiseIncidentFormState = {
  severity: 'L4',
  title: '',
  description: '',
}

const getSeverityTabStyle = (level: IncidentSeverity, active: boolean) => {
  if (!active) return undefined
  const color = INCIDENT_SEVERITY_COLORS[level]
  return {
    background: 'transparent',
    color,
    border: `1px solid ${color}`,
  }
}

export const RaiseIncidentModal = ({
  closeModal,
  params,
}: {
  closeModal: () => void
  params?: any
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [isSaving, setIsSaving] = useState(false)
  const [state, setState] = useState<IRaiseIncidentFormState>(initialState)

  useFocusFirstInput()
  useModalClose(closeModal)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    const result = await dispatch(raiseIncident({ state, params }))
    setIsSaving(false)
    if (raiseIncident.fulfilled.match(result)) {
      closeModal()
    }
  }

  return (
    <Modal black>
      <Box style={{ position: 'relative', width: '100%' }}>
        {isSaving && (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: tokens.surface.glass,
              zIndex: 1,
              borderRadius: '0.5rem',
            }}
          >
            <JustSpinner />
          </Box>
        )}
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold' style={{ textTransform: 'uppercase' }}>
              Raise Incident
            </TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>

          <Flex column gap='0.5rem' width='100%'>
            <OtherText grey>Severity</OtherText>
            <TabsContainer>
              {SEVERITY_LEVELS.map((level) => (
                <Tabs
                  key={level}
                  active={state.severity === level}
                  onClick={() => setState((prev) => ({ ...prev, severity: level }))}
                  style={getSeverityTabStyle(level, state.severity === level)}
                >
                  {level}
                </Tabs>
              ))}
            </TabsContainer>
            <OtherText grey>{INCIDENT_SEVERITY_DESCRIPTIONS[state.severity]}</OtherText>
          </Flex>

          {state.severity === 'L4' && (
            <Box
              padding='1rem'
              style={{
                border: `1px solid ${tokens.status.warning.fg}`,
                borderRadius: '0.5rem',
              }}
            >
              <BodyText style={{ color: tokens.status.warning.fg }}>
                L4 incidents trigger a permit-safety suspend cascade on the affected zones once
                persisted.
              </BodyText>
            </Box>
          )}

          <Input
            nomarg
            name='title'
            labelText='Title'
            placeholder='Enter incident title...'
            value={state.title}
            onChange={handleChange}
          />

          <Input
            nomarg
            name='description'
            type='textarea'
            height='10rem'
            labelText='Description (optional)'
            value={state.description}
            onChange={handleChange}
          />
        </Flex>

        <Flex gap='1rem' width='100%' mt='16'>
          <Button variant='secondary' style={{ width: '100%' }} onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant='primary'
            style={{ width: '100%' }}
            disabled={!state.title.trim() || isSaving}
            onClick={handleSave}
          >
            Raise Incident
          </Button>
        </Flex>
      </Flex>
      </Box>
    </Modal>
  )
}
